import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-single-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './single-select.component.html',
  styleUrl: './single-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleSelectComponent),
      multi: true
    }
  ]
})
export class SingleSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() options: any[] = [];
  @Input() optionLabel: string | undefined = undefined;
  @Input() placeholder: string = 'Select option';
  @Input() styleClass: string = '';
  @Input() disabled: boolean = false;
  @Input() showClear: boolean = true;
  /** Show a spinner row at the bottom while more options are being fetched. */
  @Input() loadingMore: boolean = false;
  /** When true, shows a search box at the top of the dropdown; parent should debounce/filter (e.g. server search). */
  @Input() filterable: boolean = false;
  @Input() filterPlaceholder: string = 'Search...';
  /** When filterable + parent is refetching options (e.g. API search). */
  @Input() optionsLoading: boolean = false;

  @Output() onChange = new EventEmitter<any>();
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();
  /** Emitted when the dropdown list is scrolled near the bottom (for infinite-scroll / lazy loading). */
  @Output() onScrollEnd = new EventEmitter<void>();
  /** Raw search text whenever the filter input changes (parent typically debounces and runs remote search). */
  @Output() filterChange = new EventEmitter<string>();

  selectedValue: any = null;
  /** Filter text shown while the dropdown is open (filterable mode). */
  filterText = '';
  isOpen = false;
  
  private onChangeCallback = (value: any) => {};
  private onTouchedCallback = () => {};

  private _docClickHandler = (event: MouseEvent): void => {
    this.onDocumentClick(event);
  };

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    document.addEventListener('click', this._docClickHandler, { capture: true });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this._docClickHandler, { capture: true });
  }

  /** Capture phase so sibling filter controls still count as outside-click when bubble is stopped upstream. */
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      const wasOpen = this.isOpen;
      this.isOpen = !this.isOpen;
      if (this.isOpen && !wasOpen) {
        this.onTouchedCallback();
        this.onOpen.emit();
      } else if (!this.isOpen && wasOpen) {
        this.onClose.emit();
      }
    }
  }

  onFilterInput(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.filterText = v;
    this.filterChange.emit(v);
  }

  clearFilterSearch(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.filterText = '';
    this.filterChange.emit('');
  }

  onFilterKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  closeDropdown(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.onClose.emit();
    }
  }

  getDisplayValue(): string {
    if (!this.selectedValue) {
      return this.placeholder;
    }
    return this.getOptionLabel(this.selectedValue);
  }

  getOptionLabel(option: any): string {
    if (this.optionLabel && typeof option === 'object' && option !== null) {
      return option[this.optionLabel] || String(option);
    }
    return String(option);
  }

  getOptionValue(option: any): any {
    return option;
  }

  isSelected(option: any): boolean {
    if (!this.selectedValue) {
      return false;
    }
    const optionValue = this.getOptionValue(option);
    
    // Compare by value property if both are objects with value
    if (typeof this.selectedValue === 'object' && typeof optionValue === 'object' && 
        this.selectedValue !== null && optionValue !== null) {
      if ('value' in this.selectedValue && 'value' in optionValue) {
        return this.selectedValue.value === optionValue.value;
      }
      if (this.optionLabel) {
        return this.selectedValue[this.optionLabel] === optionValue[this.optionLabel];
      }
    }
    
    // Direct comparison
    if (typeof this.selectedValue === 'object' && this.selectedValue !== null && 'value' in this.selectedValue) {
      const selectedVal = this.selectedValue.value;
      if (typeof optionValue === 'object' && optionValue !== null && 'value' in optionValue) {
        return selectedVal === optionValue.value;
      }
      return selectedVal === optionValue;
    }
    
    return this.selectedValue === optionValue;
  }

  selectOption(option: any): void {
    if (this.disabled) {
      return;
    }
    
    const optionValue = this.getOptionValue(option);
    
    // If clicking the same option, deselect it
    if (this.isSelected(option)) {
      this.selectedValue = null;
      this.onChangeCallback(null);
      this.onChange.emit(null);
    } else {
      this.selectedValue = optionValue;
      // Return the value property if it exists, otherwise the whole object
      const formValue = typeof optionValue === 'object' && optionValue !== null && 'value' in optionValue 
        ? optionValue.value 
        : optionValue;
      this.onChangeCallback(formValue);
      this.onChange.emit(formValue);
    }
    
    this.closeDropdown();
  }

  clearSelection(event?: Event): void {
    if (this.disabled) {
      return;
    }
    if (event) {
      event.stopPropagation();
    }
    this.selectedValue = null;
    this.onChangeCallback(null);
    this.onChange.emit(null);
  }

  onDropdownScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const threshold = 40; // px from bottom to trigger
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
      this.onScrollEnd.emit();
    }
  }

  trackByOption(index: number, option: any): any {
    if (this.optionLabel && typeof option === 'object' && option !== null) {
      return option[this.optionLabel] || index;
    }
    return option || index;
  }

  getOptionClasses(option: any): string {
    const baseClasses = 'flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors';
    if (this.isSelected(option)) {
      return `${baseClasses} bg-[#f0f7f5]`;
    }
    return baseClasses;
  }

  writeValue(value: any): void {
    if (!value) {
      this.selectedValue = null;
      return;
    }
    
    // If value is a string/number, find the matching option
    if (typeof value === 'string' || typeof value === 'number') {
      const option = this.options.find(opt => {
        const optValue = this.getOptionValue(opt);
        if (this.optionLabel && typeof optValue === 'object') {
          return optValue[this.optionLabel] === value || optValue.value === value;
        }
        return optValue === value || (typeof optValue === 'object' && optValue.value === value);
      });
      this.selectedValue = option ? this.getOptionValue(option) : null;
    } else {
      // Value is already an option object
      this.selectedValue = value;
    }
  }
  
  // Helper to get the actual value to store in form
  getFormValue(): any {
    if (!this.selectedValue) {
      return null;
    }
    
    // If option has a 'value' property, return that, otherwise return the whole object
    if (typeof this.selectedValue === 'object' && this.selectedValue !== null && 'value' in this.selectedValue) {
      return this.selectedValue.value;
    }
    
    return this.selectedValue;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
